import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faCircleCheck,
  faCircleExclamation,
  faCircleXmark,
  faClipboardList,
  faHourglassHalf,
} from "@fortawesome/free-solid-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useEffect, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import GetUser from "../utils/getUser";
import UseGraph from "../utils/useGraph";
import formQueryUrl from "../utils/formQueryUrl";

type WidgetItem = {
  status: string;
  label: string;
  icon: IconDefinition;
  iconClass: string;
  ringClass: string;
  barClass: string;
};

const widgetData: WidgetItem[] = [
  {
    status: "Awaiting-Customer",
    label: "Awaiting customer",
    icon: faHourglassHalf,
    iconClass: "text-amber-500",
    ringClass: "bg-amber-500/10",
    barClass: "bg-amber-500",
  },
  {
    status: "Pending-Review",
    label: "Pending review",
    icon: faClipboardList,
    iconClass: "text-indigo-500",
    ringClass: "bg-indigo-500/10",
    barClass: "bg-indigo-500",
  },
  {
    status: "Rejected",
    label: "Rejected",
    icon: faCircleExclamation,
    iconClass: "text-sky-500",
    ringClass: "bg-sky-500/10",
    barClass: "bg-sky-500",
  },
  {
    status: "Approved",
    label: "Approved",
    icon: faCircleCheck,
    iconClass: "text-emerald-500",
    ringClass: "bg-emerald-500/10",
    barClass: "bg-emerald-500",
  },
  {
    status: "Failed",
    label: "Failed",
    icon: faCircleXmark,
    iconClass: "text-orange-500",
    ringClass: "bg-orange-500/10",
    barClass: "bg-orange-500",
  },
  {
    status: "Actioned",
    label: "Actioned",
    icon: faBan,
    iconClass: "text-rose-500",
    ringClass: "bg-rose-500/10",
    barClass: "bg-rose-500",
  },
];

const formatCount = (value: number) => new Intl.NumberFormat("en-GB").format(value);

export default function TopBarWidget() {
  const user = GetUser();
  const [loading, setLoading] = useState<boolean>(false);
  const [widgetCount, setWidgetCount] = useState<Record<string, any>[]>([]);
  const isMyCustomer = window.location.pathname === "/my-customers";
  const { startDate, endDate } = UseGraph();

  useEffect(() => {
    const findWidgets = async () => {
      setLoading(true);
      const filtersArray = [
        isMyCustomer && { myCustomer: user?.code },
        startDate !== null && { startDate },
        endDate !== null && { endDate },
      ];
      try {
        const widgetData = await axiosInstance.get(
          `/stats/chase/counts${formQueryUrl(filtersArray, "?")}`
        );
        setWidgetCount(widgetData.data);
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    findWidgets();
  }, [user?.code, isMyCustomer, startDate, endDate]);

  const actionedData = ["Cancelled", "Rebroked", "Mismatched"];

  const counts = widgetData.map((item) => {
    if (item.status === "Actioned") {
      return widgetCount.reduce((prevWC: number, newWC: any) => {
        return (prevWC += actionedData?.includes(newWC?.status)
          ? Number(newWC?.count)
          : 0);
      }, 0);
    }

    const match = widgetCount.find((widgetC) => widgetC.status === item.status);
    return match?.count ? Number(match.count) : 0;
  });

  const total = counts.reduce((sum, count) => sum + count, 0);

  return (
    <div className="grid grid-cols-3 gap-4 desktop:grid-cols-2 smallTablet:grid-cols-1">
      {widgetData.map((item, index) => {
        const count = counts[index];
        const share = total > 0 ? Math.round((count / total) * 100) : 0;

        if (loading) {
          return (
            <div
              className="animate-pulse w-full h-[104px] bg-surface border border-edge rounded-2xl"
              key={item.status}
            />
          );
        }

        return (
          <div
            className="bg-surface border border-edge rounded-2xl shadow-card px-5 py-4 hover:border-edge-strong transition-colors duration-200 animate-fade-in"
            key={item.status}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${item.ringClass}`}
              >
                <FontAwesomeIcon
                  icon={item.icon}
                  className={`text-[16px] ${item.iconClass}`}
                />
              </div>
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-light-gray truncate">
                  {item.label}
                </p>
                <p className="text-[26px] leading-8 font-bold text-text-primary tracking-tight tabular-nums">
                  {formatCount(count)}
                </p>
              </div>
              <span className="ml-auto self-start text-[13px] font-semibold text-light-gray tabular-nums">
                {share}%
              </span>
            </div>

            <div className="mt-4 h-1 w-full rounded-full bg-overlay-mid overflow-hidden">
              <div
                className={`h-full rounded-full transition-[width] duration-700 ease-out ${item.barClass}`}
                style={{ width: `${Math.max(share, count > 0 ? 2 : 0)}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
